import retry from '@/helpers/retry';
import { getBentoStreamRoom } from '@/apis/bentoStream';
import { BentoStreamRoom } from '@/types/bentoStream';

export const RESTART_IDLE_INTERVAL_MILLISECONDS = 9 * 60 * 1000; // 9 minutes
export const RECONNECT_INTERVAL_MILLISECONDS = 105 * 60 * 1000; // 105 minutes

export const _getRoomAccess = async (
  billId: string
): Promise<BentoStreamRoom> => {
  console.info('BentoStream - access room attempt');

  try {
    const { accessToken, websocketUrl } = await getBentoStreamRoom(billId);
    return { accessToken, websocketUrl };
  } catch (error: any) {
    const { response = {} } = error;
    const details = { status: response.status, data: response.data };

    console.warn(
      `BentoStream - could not connect to room: ${JSON.stringify(details)}`
    );

    throw error;
  }
};

const createWebSocket = async (
  onMessage: (event: MessageEvent) => void,
  billId: string
): Promise<WebSocket | null> => {
  let webSocket: WebSocket | null = null;

  const _sendRestartIdleIntervalMessage = (): void => {
    if (webSocket && webSocket.readyState === WebSocket.OPEN) {
      webSocket.send('Restart IDLE Connection Timeout');
    }
  };

  const _restartIdleConnectionInterval = (): void => {
    clearInterval(_sendKeepAliveMessageInterval);
    _sendKeepAliveMessageInterval = setInterval(
      _sendRestartIdleIntervalMessage,
      RESTART_IDLE_INTERVAL_MILLISECONDS
    );
  };

  const _connect = async (): Promise<WebSocket> => {
    const roomAccess = await retry(_getRoomAccess, 2, 1000, 0, [billId]);

    console.info('BentoStream - WS connection attempt');

    webSocket = new WebSocket(
      `${roomAccess.websocketUrl}?accessToken=${roomAccess.accessToken}`
    );

    _addEventListeners(webSocket);

    return webSocket;
  };

  const _addEventListeners = (webSocket: WebSocket): void => {
    webSocket.addEventListener('message', (event: MessageEvent): void => {
      onMessage(event);
      _restartIdleConnectionInterval();
    });

    webSocket.addEventListener('close', (): void => {
      clearInterval(_reconnectInterval);
      clearInterval(_sendKeepAliveMessageInterval);
      console.info('BentoStream - WS connection closed');
    });

    webSocket.addEventListener('error', (): void => {
      console.warn('BentoStream - WS connection error');
    });

    webSocket.addEventListener('open', (): void => {
      console.info('BentoStream - WS connection open');
    });
  };

  const _reconnect = async (): Promise<void> => {
    if (webSocket) {
      webSocket.close();
    }

    await _connect();
  };

  /* Amazon API Gateway has two limitations that cannot be changed:
   * - 'Idle Connection Timeout' of 10 minutes: To avoid hitting the IDLE
   *   timeout, the WebSocket client posts a message to the connection every
   *   9 minutes of idle time.
   * - 'Connection duration for WebSocket API' of 2 hours: Before hitting the
   *   max duration of the connection we reconnect every interval of 105 minutes.
   * See: https://docs.aws.amazon.com/apigateway/latest/developerguide/limits.html
   */
  let _sendKeepAliveMessageInterval = setInterval(
    _sendRestartIdleIntervalMessage,
    RESTART_IDLE_INTERVAL_MILLISECONDS
  );
  const _reconnectInterval = setInterval(
    _reconnect,
    RECONNECT_INTERVAL_MILLISECONDS
  );

  await _connect();
  return webSocket;
};

export default createWebSocket;
